import React, { useState, useEffect } from 'react';
import MarginCalculator from './components/MarginCalculator';
import GstCalculator from './components/GstCalculator';
import GeneralCalculator from './components/GeneralCalculator';
import ThemeToggle from './components/ThemeToggle';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';

const { FiPercent, FiDollarSign, FiHash } = FiIcons;

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('margin');

  // Lifted State for Margin Calculator
  const [marginValues, setMarginValues] = useState({
    costPrice: '',
    marginPercentage: '30.00',
    profit: '',
    revenue: '',
    qty: '1',
  });
  const [stickyMargin, setStickyMargin] = useState(true);
  const [stickyMarginValue, setStickyMarginValue] = useState('30.00');

  // Lifted State for GST Calculator
  const [gstValues, setGstValues] = useState({
    base: '',
    gst: '',
    total: '',
  });

  // Lifted State for General Calculator
  const [calcExpression, setCalcExpression] = useState('');
  const [calcResult, setCalcResult] = useState('0');
  const [calcHistory, setCalcHistory] = useState([]);
  const [calcIsFinalized, setCalcIsFinalized] = useState(false);

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode !== null) {
      setDarkMode(JSON.parse(savedDarkMode));
    }
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const tabs = [
    { id: 'margin', label: 'Margin', icon: FiPercent },
    { id: 'gst', label: 'GST', icon: FiDollarSign },
    { id: 'general', label: 'Calculator', icon: FiHash },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center p-4 md:p-8 selection:bg-blue-100 dark:selection:bg-blue-900 transition-colors duration-300">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle darkMode={darkMode} toggleDarkMode={() => setDarkMode(!darkMode)} />
      </div>

      <div className="w-full max-w-lg mb-8 text-center mt-8">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-2 tracking-tight">
          Business Tools
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
          Professional tools for your daily operations
        </p>
      </div>

      {/* Slimline Tabs */}
      <div className="w-full max-w-md bg-white dark:bg-gray-800 p-1.5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center space-x-2 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={tab.icon} className="text-lg" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Dynamic Content Container - Using hidden class for instant switching */}
      <div className="w-full max-w-lg flex flex-col items-center relative">
        <div className={`w-full ${activeTab === 'margin' ? 'block' : 'hidden'}`}>
          <MarginCalculator
            darkMode={darkMode}
            values={marginValues}
            setValues={setMarginValues}
            stickyMargin={stickyMargin}
            setStickyMargin={setStickyMargin}
            stickyMarginValue={stickyMarginValue}
            setStickyMarginValue={setStickyMarginValue}
          />
        </div>

        <div className={`w-full ${activeTab === 'gst' ? 'block' : 'hidden'}`}>
          <GstCalculator
            darkMode={darkMode}
            values={gstValues}
            setValues={setGstValues}
          />
        </div>

        <div className={`w-full ${activeTab === 'general' ? 'block' : 'hidden'}`}>
          <GeneralCalculator
            darkMode={darkMode}
            isActive={activeTab === 'general'}
            expression={calcExpression}
            setExpression={setCalcExpression}
            result={calcResult}
            setResult={setCalcResult}
            history={calcHistory}
            setHistory={setCalcHistory}
            isFinalized={calcIsFinalized}
            setIsFinalized={setCalcIsFinalized}
          />
        </div>
      </div>

      <div className="mt-12 text-center opacity-50 hover:opacity-100 transition-opacity">
        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.3em]">
          By Geoff Woodberry
        </p>
      </div>
    </div>
  );
}

export default App;